import type { CSSProperties, ReactNode } from "react";
import React from "react";

import { InteractiveSurface } from "./InteractiveSurface";
import { Surface } from "./Surface";
import "./kit.css";

export type T_ListRowTone = "danger" | "default";

export type T_ListRowProps = {
  ariaLabel?: string;
  disabled?: boolean;
  leading?: ReactNode;
  onClick?: () => void;
  subtitle?: string;
  title: string;
  tone?: T_ListRowTone;
  trailing?: ReactNode;
};

function Chevron() {
  return (
    <svg
      aria-hidden
      className="lk-list-row__chevron"
      fill="none"
      height={18}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      viewBox="0 0 24 24"
      width={18}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Canonical navigation/settings row — web mirror of the mobile ListRow.
 * Fill-only pressed feedback via InteractiveSurface; interactive rows get a
 * trailing chevron by default. Keyboard access is native focus order
 * (recorded divergence: no haptics; hover/focus-visible are web-only).
 */
export function ListRow({
  ariaLabel,
  disabled = false,
  leading,
  onClick,
  subtitle,
  title,
  tone = "default",
  trailing,
}: T_ListRowProps) {
  const isInteractive = onClick !== undefined;
  const trailingContent =
    trailing !== undefined ? trailing : isInteractive ? <Chevron /> : null;
  const hasSubtitle = subtitle !== undefined && subtitle !== "";

  const content = (
    <>
      {leading}
      <span className="lk-list-row__copy">
        <span
          className={[
            "lk-list-row__title",
            tone === "danger" ? "lk-list-row__title--danger" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {title}
        </span>
        {hasSubtitle ? (
          <span className="lk-list-row__subtitle">{subtitle}</span>
        ) : null}
      </span>
      {trailingContent}
    </>
  );

  if (!isInteractive) {
    return (
      <div
        className={["lk-list-row", disabled ? "lk-list-row--disabled" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {content}
      </div>
    );
  }

  return (
    <InteractiveSurface
      ariaLabel={ariaLabel ?? title}
      backgroundColor="transparent"
      className={[
        "lk-list-row",
        "lk-list-row--interactive",
        disabled ? "lk-list-row--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
      pressedBackgroundColor="var(--lattice-dark-grey-dark-4)"
    >
      {content}
    </InteractiveSurface>
  );
}

export type T_ListGroupProps = {
  children: ReactNode;
  spaced?: boolean;
  style?: CSSProperties;
};

/** Grouped list container — web mirror of the mobile ListGroup. */
export function ListGroup({
  children,
  spaced = false,
  style,
}: T_ListGroupProps) {
  return (
    <Surface
      padding="none"
      style={{ ...(spaced ? { marginTop: 16 } : {}), ...style }}
      tone="subtle"
    >
      {children}
    </Surface>
  );
}
